import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { AppSvgIconComponent, State } from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { PageHeaderComponent } from '@core/components/page-header/page-header.component';
import { TokenStorageService } from '@core/services/token-storage.service';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type {
  FormListItemResponse,
  FormListItemResponseStatus,
  ListFormsApiResponse,
} from '@core/api/workspace/models';
import { FormListItemResponseStatus as FormStatusEnum } from '@core/api/workspace/models';
import { CreateFormFlowService } from '../forms/services/create-form-flow.service';

@Component({
  selector: 'app-dashboard-page',
  standalone: true,
  imports: [RouterLink, PageHeaderComponent, AppSvgIconComponent],
  templateUrl: './dashboard-page.html',
  styleUrl: './dashboard-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardPage extends BaseComponent implements OnInit {
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly createFormFlow = inject(CreateFormFlowService);
  private readonly workspaceApi = inject(WorkspaceAPIService);

  readonly listState = new State<ListFormsApiResponse>();
  readonly forms = signal<FormListItemResponse[]>([]);

  readonly greetingName = computed(() => {
    const user = this.tokenStorage.getUser();
    return user?.name?.trim() || 'there';
  });

  readonly workspaceName = computed(() => {
    return this.tokenStorage.getWorkspace()?.name?.trim() || 'your workspace';
  });

  readonly headerTitle = computed(() => `Welcome back, ${this.greetingName()}`);

  readonly headerDescription = computed(
    () =>
      `Here's a quick look at ${this.workspaceName()}. Create a form with AI or jump back into recent work.`,
  );

  readonly totalForms = computed(() => this.forms().length);

  readonly publishedCount = computed(
    () =>
      this.forms().filter((form) => form.status === FormStatusEnum.published)
        .length,
  );

  readonly draftCount = computed(
    () =>
      this.forms().filter((form) => form.status === FormStatusEnum.draft)
        .length,
  );

  /** No submissions API yet. */
  readonly responsesCount = computed(() => 0);

  readonly recentForms = computed(() => this.forms().slice(0, 3));

  ngOnInit(): void {
    this.loadForms();
  }

  startBuilding(): void {
    this.createFormFlow.openCreateFormModal();
  }

  formLink(form: FormListItemResponse): string[] {
    if (form.status === FormStatusEnum.published) {
      return ['/forms/published', form.slug];
    }
    return ['/forms/builder', form.slug];
  }

  formatRelativeUpdatedAt(value: string | undefined): string {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const diffMs = Date.now() - date.getTime();
    const minuteMs = 60_000;
    const hourMs = 60 * minuteMs;
    const dayMs = 24 * hourMs;

    if (diffMs < minuteMs) {
      return 'Just now';
    }
    if (diffMs < hourMs) {
      const minutes = Math.floor(diffMs / minuteMs);
      return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
    }
    if (diffMs < dayMs) {
      const hours = Math.floor(diffMs / hourMs);
      return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    }
    if (diffMs < 2 * dayMs) {
      return 'Yesterday';
    }
    if (diffMs < 7 * dayMs) {
      const days = Math.floor(diffMs / dayMs);
      return days === 1 ? '1 day ago' : `${days} days ago`;
    }

    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  getStatusClass(status: FormListItemResponseStatus): string {
    switch (status) {
      case FormStatusEnum.published:
        return 'bg-success-100 text-success-700';
      case FormStatusEnum.draft:
        return 'bg-amber-100 text-amber-700';
      case FormStatusEnum.unpublished:
        return 'bg-slate-100 text-slate-600';
      case FormStatusEnum.finished:
        return 'bg-violet-100 text-violet-700';
      default:
        return 'bg-slate-100 text-slate-600';
    }
  }

  private loadForms(): void {
    this.executeRequest({
      state: this.listState,
      request: this.workspaceApi.listForms(),
      handleError: true,
      onSuccess: (response) => {
        this.forms.set(response.data.forms ?? []);
      },
    });
  }
}
